import React, { useState } from 'react';
import { Brain, Loader2, AlertCircle, ChevronDown, ChevronRight, Shield, Lightbulb, BarChart3, Check, XCircle } from 'lucide-react';
import { useLogPatterns, useLogGroups } from '../hooks/useLogs';

const severityConfig = {
  high: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200', dot: 'bg-red-500' },
  medium: { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-200', dot: 'bg-amber-500' },
  low: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200', dot: 'bg-green-500' },
};

function PatternCard({ pattern }) {
  const [expanded, setExpanded] = useState(false);
  const sev = severityConfig[pattern.severity] || severityConfig.medium;

  return (
    <div className={`border rounded-lg overflow-hidden ${sev.border}`}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg font-semibold text-gray-900">
                #{pattern.rank} {pattern.pattern_name}
              </span>
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${sev.bg} ${sev.text}`}>
                {pattern.severity}
              </span>
            </div>
            <p className="text-sm text-gray-600">{pattern.description}</p>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="text-2xl font-bold text-gray-900">{pattern.estimated_count}</div>
            <div className="text-xs text-gray-500">{pattern.percentage}% of errors</div>
          </div>
        </div>

        {/* Recommendation */}
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex gap-2">
          <Lightbulb className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <p className="text-sm text-blue-800">{pattern.recommendation}</p>
        </div>

        {/* Sample messages toggle */}
        {pattern.sample_messages && pattern.sample_messages.length > 0 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-3 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            {expanded ? 'Hide' : 'Show'} sample errors ({pattern.sample_messages.length})
          </button>
        )}
      </div>

      {expanded && pattern.sample_messages && (
        <div className="border-t border-gray-200 bg-gray-50 p-4 space-y-2">
          {pattern.sample_messages.map((msg, i) => (
            <div key={i} className="font-mono text-xs text-gray-700 bg-white p-2 rounded border border-gray-200 break-all">
              {msg}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function LogPatterns() {
  const { mutate, data, isPending, isError, error, isSuccess, cancel } = useLogPatterns();
  const { data: groupsData } = useLogGroups();
  const [days, setDays] = useState(7);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [groupsExpanded, setGroupsExpanded] = useState(false);

  const allGroups = groupsData?.data?.data || groupsData?.data || [];
  const groupNames = allGroups.map((g) => g.name || g);

  const toggleGroup = (name) => {
    setSelectedGroups((prev) =>
      prev.includes(name) ? prev.filter((g) => g !== name) : [...prev, name]
    );
  };

  const selectAll = () => setSelectedGroups([...groupNames]);
  const selectNone = () => setSelectedGroups([]);

  const handleAnalyze = () => {
    const params = { days };
    if (selectedGroups.length > 0 && selectedGroups.length < groupNames.length) {
      params.logGroups = selectedGroups;
    }
    mutate(params);
  };

  const result = data?.data?.data || data?.data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
            <Brain className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Log Patterns</h1>
            <p className="text-sm text-gray-500">AI-powered error analysis using Amazon Bedrock</p>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="card">
        <div className="flex items-end gap-4 flex-wrap">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Time Range</label>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            >
              <option value={1}>Last 24 hours</option>
              <option value={3}>Last 3 days</option>
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
            </select>
          </div>

          <div>
            <button
              onClick={handleAnalyze}
              disabled={isPending}
              className="px-4 py-2 bg-amber-400 text-gray-900 rounded-lg hover:bg-amber-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 text-sm font-medium"
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Brain className="w-4 h-4" />
                  Analyze Error Patterns
                </>
              )}
            </button>
          </div>
        </div>

        {/* Log Group Selector */}
        {groupNames.length > 0 && (
          <div className="mt-4">
            <button
              onClick={() => setGroupsExpanded(!groupsExpanded)}
              className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              {groupsExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              Log Groups ({selectedGroups.length === 0 ? `All ${groupNames.length}` : `${selectedGroups.length} of ${groupNames.length}`})
            </button>

            {groupsExpanded && (
              <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <button onClick={selectAll} className="text-xs text-amber-600 hover:text-amber-800 font-medium">Select All</button>
                  <button onClick={selectNone} className="text-xs text-amber-600 hover:text-amber-800 font-medium">Clear All</button>
                  <span className="text-xs text-gray-400">Leave empty to search all groups</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1">
                  {groupNames.map((name) => (
                    <label
                      key={name}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-xs hover:bg-gray-100 ${
                        selectedGroups.includes(name) ? 'bg-amber-50 text-amber-900' : 'text-gray-700'
                      }`}
                    >
                      <div
                        className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                          selectedGroups.includes(name)
                            ? 'bg-amber-400 border-amber-400'
                            : 'border-gray-300 bg-white'
                        }`}
                        onClick={() => toggleGroup(name)}
                      >
                        {selectedGroups.includes(name) && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span className="truncate" onClick={() => toggleGroup(name)}>{name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {isPending && (
          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 text-amber-600 animate-spin" />
                <div>
                  <p className="text-sm font-medium text-amber-900">Analyzing error patterns...</p>
                  <p className="text-xs text-amber-700 mt-1">
                    Fetching error logs from CloudWatch and running AI analysis. This may take up to a minute.
                  </p>
                </div>
              </div>
              <button
                onClick={cancel}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
              >
                <XCircle className="w-4 h-4" />
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Error */}
      {isError && (
        <div className="card border-red-200 bg-red-50">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <div>
              <p className="font-medium text-red-900">Analysis failed</p>
              <p className="text-sm text-red-700">{error?.message || 'An unexpected error occurred'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      {isSuccess && result && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="card bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200">
              <div className="flex items-center gap-2 mb-1">
                <BarChart3 className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-900">Errors Analyzed</span>
              </div>
              <p className="text-2xl font-bold text-amber-900">{result.total_errors_analyzed || result.raw_error_count || 0}</p>
              <p className="text-xs text-amber-700 mt-1">Past {result.time_range_days || days} days</p>
            </div>

            <div className="card bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
              <div className="flex items-center gap-2 mb-1">
                <Shield className="w-4 h-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-900">Patterns Found</span>
              </div>
              <p className="text-2xl font-bold text-amber-900">{result.patterns?.length || 0}</p>
              <p className="text-xs text-amber-700 mt-1">Distinct error patterns</p>
            </div>

            <div className="card bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
              <div className="flex items-center gap-2 mb-1">
                <Brain className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-900">Log Groups</span>
              </div>
              <p className="text-2xl font-bold text-blue-900">{result.log_groups?.length || 0}</p>
              <p className="text-xs text-blue-700 mt-1">{result.log_groups?.join(', ')}</p>
            </div>
          </div>

          {/* Analysis Summary */}
          {result.analysis_summary && (
            <div className="card bg-gradient-to-r from-gray-50 to-slate-50">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">AI Analysis Summary</h3>
              <p className="text-sm text-gray-700">{result.analysis_summary}</p>
            </div>
          )}

          {/* Pattern Cards */}
          {result.patterns && result.patterns.length > 0 ? (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Error Patterns</h3>
              {result.patterns.map((pattern, i) => (
                <PatternCard key={i} pattern={pattern} />
              ))}
            </div>
          ) : (
            <div className="card text-center py-8">
              <p className="text-gray-500">No error patterns found in the specified time range.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
